import React, { useState, useRef, useEffect } from 'react';
import { Download, ChevronDown, FileSpreadsheet, Filter, Package, CreditCard, Upload, FileDown, Scale, FileSignature, Layers, Gauge, Package2, ClipboardList, Wrench } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ExcelExportDropdownProps {
  onDownloadAll: () => void;
  onDownloadCurrent?: () => void;
  onDownloadFiltered: () => void;
  onDownloadPackaging?: () => void;
  onDownloadHeader?: () => void;
  onDownloadInterProducts?: () => void;
  onDownloadProductivity?: () => void;
  onDownloadRazmer?: () => void;
  onDownloadMaterialno?: () => void;
  onDownloadInProcess?: () => void;
  onDownloadMontageParams?: () => void;
  onDownloadPaymentTerms?: () => void;
  onImportAltNames?: () => void;
  onImportPaymentTerms?: () => void;
  onDownloadTemplate?: () => void;
  onImport?: () => void;
  hasCurrentItem: boolean;
  currentItemCode?: string;
  isDisabled?: boolean;
  entityLabel?: string; // "Items", "Partners", etc.
}

const ExcelExportDropdown: React.FC<ExcelExportDropdownProps> = ({
  onDownloadAll,
  onDownloadCurrent,
  onDownloadFiltered,
  onDownloadPackaging,
  onDownloadHeader,
  onDownloadInterProducts,
  onDownloadProductivity,
  onDownloadRazmer,
  onDownloadMaterialno,
  onDownloadInProcess,
  onDownloadMontageParams,
  onDownloadPaymentTerms,
  onImportAltNames,
  onImportPaymentTerms,
  onDownloadTemplate,
  onImport,
  hasCurrentItem,
  currentItemCode,
  isDisabled = false,
  entityLabel = 'Items',
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard events
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handleOptionClick = (action: () => void) => {
    action();
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef} onKeyDown={handleKeyDown}>
      {/* Dropdown trigger button */}
      <button
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
        className={`p-1.5 h-9 rounded-lg transition-all duration-200 flex items-center justify-center gap-1 ${
          isDisabled
            ? 'text-gray-400 cursor-not-allowed'
            : 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50'
        }`}
        disabled={isDisabled}
        title={t('excel.export.title') || 'Export to Excel'}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <Download size={20} />
        <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          className="absolute right-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
          role="menu"
          aria-orientation="vertical"
        >
          {/* Download All */}
          <button
            onClick={() => handleOptionClick(onDownloadAll)}
            className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 flex items-center gap-3 transition-colors"
            role="menuitem"
          >
            <FileSpreadsheet size={18} className="text-emerald-600" />
            <span>{t('excel.export.downloadAll', { entity: entityLabel }) || `Download All ${entityLabel}`}</span>
          </button>

          {/* Download Current Item — optional: /os omits it in favour of the
              per-section exports below, which read the same data the screen shows. */}
          {onDownloadCurrent && (
          <button
            onClick={() => hasCurrentItem && handleOptionClick(onDownloadCurrent)}
            className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 transition-colors ${
              hasCurrentItem
                ? 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                : 'text-gray-400 cursor-not-allowed'
            }`}
            disabled={!hasCurrentItem}
            role="menuitem"
          >
            <Download size={18} className={hasCurrentItem ? 'text-emerald-600' : 'text-gray-400'} />
            <span>
              {t('excel.export.downloadCurrent') || 'Download Current'}
              {hasCurrentItem && currentItemCode && (
                <span className="ml-1 text-xs text-gray-500">({currentItemCode})</span>
              )}
            </span>
          </button>
          )}

          {/* Заглавие for the current item — only shown when handler provided */}
          {onDownloadHeader && (
            <button
              onClick={() => hasCurrentItem && handleOptionClick(onDownloadHeader)}
              className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 transition-colors ${
                hasCurrentItem
                  ? 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                  : 'text-gray-400 cursor-not-allowed'
              }`}
              disabled={!hasCurrentItem}
              role="menuitem"
            >
              <FileSignature size={18} className={hasCurrentItem ? 'text-emerald-600' : 'text-gray-400'} />
              <span>
                {t('excel.export.downloadHeader') || 'Преземи Заглавие'}
                {hasCurrentItem && currentItemCode && (
                  <span className="ml-1 text-xs text-gray-500">({currentItemCode})</span>
                )}
              </span>
            </button>
          )}

          {/* Меѓу/Полупроизвод for the current item — only shown when handler provided */}
          {onDownloadInterProducts && (
            <button
              onClick={() => hasCurrentItem && handleOptionClick(onDownloadInterProducts)}
              className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 transition-colors ${
                hasCurrentItem
                  ? 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                  : 'text-gray-400 cursor-not-allowed'
              }`}
              disabled={!hasCurrentItem}
              role="menuitem"
            >
              <Layers size={18} className={hasCurrentItem ? 'text-emerald-600' : 'text-gray-400'} />
              <span>
                {t('excel.export.downloadInterProducts') || 'Преземи Меѓу/Полупроизвод'}
                {hasCurrentItem && currentItemCode && (
                  <span className="ml-1 text-xs text-gray-500">({currentItemCode})</span>
                )}
              </span>
            </button>
          )}

          {/* Продуктивност for the current item — only shown when handler provided */}
          {onDownloadProductivity && (
            <button
              onClick={() => hasCurrentItem && handleOptionClick(onDownloadProductivity)}
              className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 transition-colors ${
                hasCurrentItem
                  ? 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                  : 'text-gray-400 cursor-not-allowed'
              }`}
              disabled={!hasCurrentItem}
              role="menuitem"
            >
              <Gauge size={18} className={hasCurrentItem ? 'text-emerald-600' : 'text-gray-400'} />
              <span>
                {t('excel.export.downloadProductivity') || 'Преземи Log Book'}
                {hasCurrentItem && currentItemCode && (
                  <span className="ml-1 text-xs text-gray-500">({currentItemCode})</span>
                )}
              </span>
            </button>
          )}

          {/* Размерување for the current item — only shown when handler provided */}
          {onDownloadRazmer && (
            <button
              onClick={() => hasCurrentItem && handleOptionClick(onDownloadRazmer)}
              className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 transition-colors ${
                hasCurrentItem
                  ? 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                  : 'text-gray-400 cursor-not-allowed'
              }`}
              disabled={!hasCurrentItem}
              role="menuitem"
            >
              <Scale size={18} className={hasCurrentItem ? 'text-emerald-600' : 'text-gray-400'} />
              <span>
                {t('excel.export.downloadRazmer') || 'Преземи Размерување'}
                {hasCurrentItem && currentItemCode && (
                  <span className="ml-1 text-xs text-gray-500">({currentItemCode})</span>
                )}
              </span>
            </button>
          )}

          {/* Материјално for the current item — only shown when handler provided */}
          {onDownloadMaterialno && (
            <button
              onClick={() => hasCurrentItem && handleOptionClick(onDownloadMaterialno)}
              className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 transition-colors ${
                hasCurrentItem
                  ? 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                  : 'text-gray-400 cursor-not-allowed'
              }`}
              disabled={!hasCurrentItem}
              role="menuitem"
            >
              <Package2 size={18} className={hasCurrentItem ? 'text-emerald-600' : 'text-gray-400'} />
              <span>
                {t('excel.export.downloadMaterialno') || 'Преземи Материјално'}
                {hasCurrentItem && currentItemCode && (
                  <span className="ml-1 text-xs text-gray-500">({currentItemCode})</span>
                )}
              </span>
            </button>
          )}

          {/* InProcess for the current item — only shown when handler provided */}
          {onDownloadInProcess && (
            <button
              onClick={() => hasCurrentItem && handleOptionClick(onDownloadInProcess)}
              className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 transition-colors ${
                hasCurrentItem
                  ? 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                  : 'text-gray-400 cursor-not-allowed'
              }`}
              disabled={!hasCurrentItem}
              role="menuitem"
            >
              <ClipboardList size={18} className={hasCurrentItem ? 'text-emerald-600' : 'text-gray-400'} />
              <span>
                {t('excel.export.downloadInProcess') || 'Преземи InProcess'}
                {hasCurrentItem && currentItemCode && (
                  <span className="ml-1 text-xs text-gray-500">({currentItemCode})</span>
                )}
              </span>
            </button>
          )}

          {/* Параметри за монтажа for the current item — only shown when handler provided */}
          {onDownloadMontageParams && (
            <button
              onClick={() => hasCurrentItem && handleOptionClick(onDownloadMontageParams)}
              className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 transition-colors ${
                hasCurrentItem
                  ? 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                  : 'text-gray-400 cursor-not-allowed'
              }`}
              disabled={!hasCurrentItem}
              role="menuitem"
            >
              <Wrench size={18} className={hasCurrentItem ? 'text-emerald-600' : 'text-gray-400'} />
              <span>
                {t('excel.export.downloadMontageParams') || 'Преземи Параметри за монтажа'}
                {hasCurrentItem && currentItemCode && (
                  <span className="ml-1 text-xs text-gray-500">({currentItemCode})</span>
                )}
              </span>
            </button>
          )}

          {/* Divider */}
          <div className="border-t border-gray-100 my-1" />

          {/* Download with Filters */}
          <button
            onClick={() => handleOptionClick(onDownloadFiltered)}
            className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 flex items-center gap-3 transition-colors"
            role="menuitem"
          >
            <Filter size={18} className="text-emerald-600" />
            <span>{t('excel.export.withFilters') || 'Download with Filters...'}</span>
          </button>

          {/* Template download — only shown when handler provided */}
          {onDownloadTemplate && (
            <>
              <div className="border-t border-gray-100 my-1" />
              <button
                onClick={() => handleOptionClick(onDownloadTemplate)}
                className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 flex items-center gap-3 transition-colors"
                role="menuitem"
              >
                <FileDown size={18} className="text-emerald-600" />
                <span>{t('excel.export.downloadTemplate') || 'Преземи шаблон'}</span>
              </button>
            </>
          )}

          {/* Packaging export — only shown when handler provided */}
          {onDownloadPackaging && (
            <>
              <div className="border-t border-gray-100 my-1" />
              <button
                onClick={() => handleOptionClick(onDownloadPackaging)}
                className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 flex items-center gap-3 transition-colors"
                role="menuitem"
              >
                <Package size={18} className="text-emerald-600" />
                <span>{t('excel.export.downloadPackaging') || 'Преземи Амбалажа'}</span>
              </button>
            </>
          )}

          {/* Payment terms export — only shown when handler provided */}
          {onDownloadPaymentTerms && (
            <>
              <div className="border-t border-gray-100 my-1" />
              <button
                onClick={() => handleOptionClick(onDownloadPaymentTerms)}
                className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 flex items-center gap-3 transition-colors"
                role="menuitem"
              >
                <CreditCard size={18} className="text-emerald-600" />
                <span>{t('excel.export.downloadPaymentTerms') || 'Преземи Начин на плаќање'}</span>
              </button>
            </>
          )}

          {/* Import options — only shown when handlers provided */}
          {(onImport || onImportAltNames || onImportPaymentTerms) && (
            <div className="border-t border-gray-100 my-1" />
          )}
          {onImport && (
            <button
              onClick={() => handleOptionClick(onImport)}
              className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 flex items-center gap-3 transition-colors"
              role="menuitem"
            >
              <Upload size={18} className="text-emerald-600" />
              <span>{t('excel.import.importPartners') || 'Увези партнери'}</span>
            </button>
          )}
          {onImportAltNames && (
            <button
              onClick={() => handleOptionClick(onImportAltNames)}
              className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 flex items-center gap-3 transition-colors"
              role="menuitem"
            >
              <Upload size={18} className="text-emerald-600" />
              <span>{t('excel.import.importAltNames')}</span>
            </button>
          )}
          {onImportPaymentTerms && (
            <button
              onClick={() => handleOptionClick(onImportPaymentTerms)}
              className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 flex items-center gap-3 transition-colors"
              role="menuitem"
            >
              <Upload size={18} className="text-emerald-600" />
              <span>{t('excel.import.importPaymentTerms')}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ExcelExportDropdown;
